import { makeAutoObservable } from 'mobx';
import api from '../config/api';
import {
    TildaForm,
    TildaFormCreateRequest,
    TildaFormUpdateRequest,
    Tag,
    Event,
    TildaPage,
    MMChannel
} from '../types';

export class TildaStore {
    forms: TildaForm[] = [];
    tags: Tag[] = [];
    events: Event[] = [];
    tildaPages: TildaPage[] = [];
    mmChannels: MMChannel[] = [];
    loading = false;
    error: string | null = null;
    saving = false;

    constructor() {
        makeAutoObservable(this);
    }

    async fetchForms() {
        this.loading = true;
        this.error = null;

        try {
            const response = await api.get<TildaForm[]>('/api/tilda-forms');
            this.forms = response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch Tilda forms';
            this.forms = [];
        } finally {
            this.loading = false;
        }
    }

    async createForm(formData: TildaFormCreateRequest): Promise<boolean> {
        this.saving = true;
        this.error = null;

        try {
            const response = await api.post<TildaForm>('/api/tilda-forms', formData);
            this.forms.push(response.data);
            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to create Tilda form';
            return false;
        } finally {
            this.saving = false;
        }
    }

    async updateForm(id: number, formData: TildaFormUpdateRequest): Promise<boolean> {
        this.saving = true;
        this.error = null;

        try {
            const response = await api.put<TildaForm>(`/api/tilda-forms/${id}`, formData);

            const index = this.forms.findIndex(f => f.id === id);
            if (index !== -1) {
                this.forms[index] = response.data;
            }

            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to update Tilda form';
            return false;
        } finally {
            this.saving = false;
        }
    }

    async deleteForm(id: number): Promise<boolean> {
        this.saving = true;
        this.error = null;

        try {
            await api.delete(`/api/tilda-forms/${id}`);
            this.forms = this.forms.filter(f => f.id !== id);
            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to delete Tilda form';
            return false;
        } finally {
            this.saving = false;
        }
    }

    async fetchTags() {
        try {
            const response = await api.get<Tag[]>('/api/tags');
            this.tags = response.data;
        } catch (error: any) {
            console.error('Failed to fetch tags:', error);
            // Fallback на моковые данные если API не доступен
            this.tags = [
                { id: 'tag1', name: 'Горячие лиды' },
                { id: 'tag2', name: 'Теплые лиды' },
                { id: 'tag3', name: 'Холодные лиды' },
                { id: 'tag4', name: 'VIP клиенты' },
                { id: 'tag5', name: 'Корпоративные клиенты' },
            ];
        }
    }

    async fetchEvents() {
        try {
            const response = await api.get<Event[]>('/api/events');
            this.events = response.data;
        } catch (error: any) {
            console.error('Failed to fetch events:', error);
            // Fallback на моковые данные если API не доступен
            this.events = [
                { id: 'event1', name: 'Вебинар по продукту' },
                { id: 'event2', name: 'Конференция 2025' },
                { id: 'event3', name: 'Мастер-класс по CRM' },
                { id: 'event4', name: 'Демо-презентация' },
                { id: 'event5', name: 'Корпоративное обучение' },
            ];
        }
    }

    async fetchTildaPages() {
        try {
            const response = await api.get<TildaPage[]>('/api/tilda-pages');
            this.tildaPages = response.data;
        } catch (error: any) {
            console.error('Failed to fetch Tilda pages:', error);
            // Fallback на моковые данные если API не доступен
            this.tildaPages = [
                { id: 'form1', name: 'Заявка на консультацию' },
                { id: 'form2', name: 'Регистрация на вебинар' },
                { id: 'form3', name: 'Обратная связь' },
                { id: 'form4', name: 'Заказ демо-версии' },
                { id: 'form5', name: 'Подписка на новости' },
                { id: 'form6', name: 'Корпоративная заявка' },
            ];
        }
    }

    async fetchMMChannels() {
        try {
            const response = await api.get<MMChannel[]>('/api/mm-channels');
            this.mmChannels = response.data;
        } catch (error: any) {
            console.error('Failed to fetch MM channels:', error);
            // Fallback на моковые данные если API не доступен
            this.mmChannels = [
                { id: 'channel1', name: 'general' },
                { id: 'channel2', name: 'random' },
                { id: 'channel3', name: 'development' },
                { id: 'channel4', name: 'marketing' },
                { id: 'channel5', name: 'sales' },
            ];
        }
    }

    getFormById(id: number): TildaForm | undefined {
        return this.forms.find(f => f.id === id);
    }

    getFormByFormId(formId: string): TildaForm | undefined {
        return this.forms.find(f => f.form_id === formId);
    }

    get activeForms(): TildaForm[] {
        return this.forms.filter(f => f.is_active);
    }

    get inactiveForms(): TildaForm[] {
        return this.forms.filter(f => !f.is_active);
    }

    get formsWithLeadCreation(): TildaForm[] {
        return this.forms.filter(f => f.create_lead);
    }

    get formsWithEventAttachment(): TildaForm[] {
        return this.forms.filter(f => f.create_event);
    }

    get formsWithEmailVerification(): TildaForm[] {
        return this.forms.filter(f => f.require_email_verification);
    }

    get formsWithMMInformer(): TildaForm[] {
        return this.forms.filter(f => f.create_mm_informer);
    }

    getTagById(id: string): Tag | undefined {
        return this.tags.find(t => t.id === id);
    }

    getEventById(id: string): Event | undefined {
        return this.events.find(e => e.id === id);
    }

    getTildaPageById(id: string): TildaPage | undefined {
        return this.tildaPages.find(p => p.id === id);
    }

    getMMChannelById(id: string): MMChannel | undefined {
        return this.mmChannels.find(c => c.id === id);
    }

    get tildaForms() {
        return this.tildaPages;
    }

    get mm_channels() {
        return this.mmChannels;
    }

    resetError() {
        this.error = null;
    }

    clearForms() {
        this.forms = [];
    }

    clearTags() {
        this.tags = [];
    }

    clearEvents() {
        this.events = [];
    }

    clearMMChannels() {
        this.mmChannels = [];
    }

    clearTildaPages() {
        this.tildaPages = [];
    }
}